import { createRouter, createWebHistory } from 'vue-router'

import { designRoutes } from '@/design/routes'
import { accessRedirect, landingRoute } from '@/libs/access'
import { isAuthenticated } from '@/libs/auth'
import { useAppStore } from '@/stores/app'

/*
 * What a route says about itself (docs/API.md → Permissions; `libs/access.ts` reads it).
 * `tenant` — lives under `/t/:tid/…`; `requiresAdmin` — admin rights in that tenant;
 * `requiresProvider` — a provider admin's page.
 */
declare module 'vue-router' {
    interface RouteMeta {
        requiresAuth?:     boolean
        requiresAdmin?:    boolean
        requiresProvider?: boolean
        tenant?:           boolean
        layout?:           'Default' | 'Blank'
    }
}

/*
 * The signed-in shell is exactly the viewport (`layouts/Default.vue`, `h-dvh`) and its `<main>`
 * is what scrolls — the window never does. So a navigation to another page scrolls THAT
 * element back to the top; a change of the query only leaves it where it is.
 */
const shellMain = (): HTMLElement | null => document.querySelector<HTMLElement>('main')

/** A tenant page: `/t/:tid/<path>`, signed in, in the shell. */
const tenantPage = (path: string, name: string, component: () => Promise<unknown>, requiresAdmin = false) => ({
    path:      `/t/:tid/${path}`,
    name,
    component,
    meta:      { requiresAuth: true, tenant: true, requiresAdmin },
})

/*
 * The design module's routes (`src/design/routes.ts`, owned by the design branch): the editor
 * `/t/:tid/designs/:id/edit` inside the shell, a tenant page like the others. (The screen
 * player `/play` is its own page, `play.html`, not a route of this app.)
 */
const designs = designRoutes.map((route) => route.name === 'design-editor'
    ? { ...route, meta: { ...route.meta, tenant: true } }
    : route)

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior(to, from, savedPosition) {
        const main = shellMain()

        if (!main) {
            if (savedPosition)         return savedPosition
            if (from.path === to.path) return {}

            return { top: 0 }
        }

        if (from.path !== to.path) main.scrollTo({ top: 0 })

        return false
    },
    routes: [
        { path: '/', redirect: landingRoute },
        // Where a person starts: their tenant's Pregled, the provider's last tenant, or Stranke.
        {
            path:      '/start',
            name:      'landing',
            component: () => import('@/views/Landing.vue'),
            meta:      { layout: 'Blank', requiresAuth: true },
        },
        {
            path:      '/login',
            name:      'login',
            component: () => import('@/views/Login.vue'),
            meta:      { layout: 'Blank', requiresAuth: false },
        },
        /*
         * DELI ZASLON — a guest's laptop shares its screen to a meeting-room screen by the code
         * that screen shows (docs/API.md → Screen sharing). Public: nobody signs in, and a
         * console session open in the same browser plays no part. `/share` is the same page.
         */
        {
            path:      '/deli',
            alias:     '/share',
            name:      'share',
            component: () => import('@/views/share/Share.vue'),
            meta:      { layout: 'Blank', requiresAuth: false },
        },

        tenantPage('overview',  'overview',     () => import('@/views/overview/Overview.vue')),
        tenantPage('screens',   'screens',      () => import('@/views/screen/Screens.vue')),
        tenantPage('media',     'media',        () => import('@/views/media/Media.vue')),
        tenantPage('menu',      'menu',         () => import('@/views/menu/Menu.vue')),
        tenantPage('templates', 'templates',    () => import('@/views/template/Templates.vue')),
        // A playlist ("Zaporedje") of Predloge; `:id` "new" is one not saved yet.
        tenantPage('playlists/:id', 'playlist-editor', () => import('@/views/playlist/Playlist.vue')),
        tenantPage('schedules', 'schedules',    () => import('@/views/schedule/Schedules.vue')),
        tenantPage('brand',     'brand',        () => import('@/views/brand/Brand.vue')),
        tenantPage('users',     'tenant-users', () => import('@/views/user/Users.vue'), true),

        ...designs,

        // The provider's pages.
        {
            path:      '/tenants',
            name:      'tenants',
            component: () => import('@/views/tenant/Tenants.vue'),
            meta:      { requiresAuth: true, requiresProvider: true },
        },
        {
            path:      '/users',
            name:      'users',
            component: () => import('@/views/user/Users.vue'),
            meta:      { requiresAuth: true, requiresProvider: true },
        },

        // Everybody's own account, and the provider's name for a provider admin.
        {
            path:      '/settings',
            name:      'settings',
            component: () => import('@/views/settings/Settings.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:     '/:pathMatch(.*)*',
            redirect: landingRoute,
        },
    ],
})

router.beforeEach((to, _from, next) => {
    const appStore = useAppStore()
    const loggedIn = isAuthenticated()

    // A public page never depends on who is signed in in this browser. (The screen player is
    // not a route of this app any more: `play.html`.)
    if (to.meta.requiresAuth === false && to.name !== 'login') return next()

    if (loggedIn && to.name === 'login') return next(landingRoute())

    if (to.meta.requiresAuth && !loggedIn) {
        appStore.setLogout()

        return next({ name: 'login', query: to.fullPath !== '/' && to.name !== 'landing' ? { redirect: to.fullPath } : {} })
    }

    /*
     * A page that is not this person's. Who they are arrives with GET /me, which App.vue asks
     * for as the console starts, so on a cold load it is not known here yet — `accessRedirect`
     * then says nothing and App.vue makes the same check once the answer is there.
     */
    const redirect = accessRedirect(to)

    if (redirect) return next(redirect)

    next()
})

/*
 * The tenant of the address is the one whose role decides what may be written
 * (`stores/app.ts` → isAdmin / canEdit): set once the navigation is confirmed, before the
 * new page is drawn.
 */
router.afterEach((to) => {
    useAppStore().setCurrentTid(to.meta.tenant && typeof to.params.tid === 'string' ? to.params.tid : '')
})

// A redeploy replaces the hashed chunk files while a browser may still hold the old
// index.html; every route is lazy, so reload once when a chunk cannot be loaded.
router.onError((error) => {
    if (/dynamically imported module|Importing a module script failed|Failed to fetch/i.test(String(error))) {
        window.location.reload()
    }
})

export default router
